import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CheckCircle2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { PRICING } from "@/lib/pricing";
import { toast } from "sonner";
import { LegalConsent } from "@/components/LegalLinks";

const allFeatures = [
  "Automatic receipt tracking (Amazon, eBay, Walmart)",
  "Spending history and pattern insights",
  "Monthly breakdown dashboard",
  "CSV export",
  "Import purchases from Gmail (phone, other browsers, past orders)",
  "Gentle budget nudges (75%, 90%, 100%)",
  "Clarity Lock (lock budget edits)",
  "Awareness streak",
  "Chrome extension with mindful nudges",
  "Private by design — we never store email content",
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [billingInterval, setBillingInterval] = useState("yearly");

  const isPremium = user?.plan === "premium";
  const isTrialActive = user?.plan === "trial" && user?.trial_end_date && new Date(user.trial_end_date) > new Date();
  const selectedPlan = PRICING[billingInterval];

  const handleCheckout = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    if (isPremium) {
      toast.info("You're already on the Premium plan");
      return;
    }
    setLoading(true);
    try {
      const res = await api.createCheckout(billingInterval);
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      if (err.response?.status === 409) {
        // Already subscribed in Stripe; the server just synced the plan, so pick it up
        toast.info(err.response.data.detail);
        await refreshUser();
      } else {
        toast.error(err.response?.data?.detail || "Failed to start checkout");
      }
    } finally {
      setLoading(false);
    }
  };

  const ctaLabel = () => {
    if (isPremium) return "You're on Premium";
    if (isTrialActive) return `Subscribe Now — ${selectedPlan.priceWithInterval}`;
    if (user) return `Subscribe — ${selectedPlan.priceWithInterval}`;
    return "Start 30-Day Free Trial";
  };

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <header className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {user ? (
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
              </Button>
            ) : (
              <Link to="/" className="flex items-center">
                <img src="/logo-lockup.svg" alt="CartControl" className="h-8 w-auto" />
              </Link>
            )}
          </div>
          {!user && (
            <Button className="bg-navy hover:bg-navy-hover active:bg-navy-active text-white text-sm font-medium h-10 px-6" onClick={() => navigate("/auth")} data-testid="pricing-login-btn">
              Log in
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-16">
        <div className="text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-navy tracking-tight font-display">
            Simple pricing
          </h1>
          <p className="mt-4 text-base text-gray-500 max-w-lg mx-auto">
            30-day free trial. Full access. No credit card required.
          </p>
        </div>

        <div className="mt-12 rounded-xl border-2 border-navy bg-white p-8 relative shadow-sm" data-testid="pricing-premium-plan">
          <Badge className="absolute -top-3 left-8 bg-navy text-white text-xs">30-Day Free Trial</Badge>
          <h3 className="text-xl font-semibold text-navy font-display">CartControl Premium</h3>

          {/* Billing interval toggle */}
          <div className="mt-4 inline-flex rounded-lg border border-border bg-gray-50 p-1" data-testid="pricing-interval-toggle">
            <button
              type="button"
              onClick={() => setBillingInterval("yearly")}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${billingInterval === "yearly" ? "bg-navy text-white" : "text-gray-500 hover:text-navy"}`}
              data-testid="pricing-interval-yearly"
            >
              Yearly
            </button>
            <button
              type="button"
              onClick={() => setBillingInterval("monthly")}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${billingInterval === "monthly" ? "bg-navy text-white" : "text-gray-500 hover:text-navy"}`}
              data-testid="pricing-interval-monthly"
            >
              Monthly
            </button>
          </div>

          <div className="mt-4 flex items-baseline gap-1">
            <span className="text-4xl font-bold text-navy font-display">{selectedPlan.price}</span>
            <span className="text-sm text-gray-500">{selectedPlan.perInterval}</span>
          </div>
          <p className="mt-1 text-sm text-gray-400">
            {billingInterval === "yearly" ? `Best value — about ${PRICING.yearly.monthlyEquivalent}/month, billed yearly` : "after your free trial"}
          </p>
          <p className="mt-3 text-sm text-gray-500">
            Or stay on <span className="font-medium text-navy">Basic (free)</span> — Amazon tracking, warnings &amp; full history, forever.
          </p>
          <ul className="mt-8 space-y-3">
            {allFeatures.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600">
                <CheckCircle2 className="w-4 h-4 text-sage mt-0.5 shrink-0" />
                {f}
              </li>
            ))}
          </ul>
          <Button
            className="mt-8 w-full h-11 bg-navy hover:bg-navy-hover active:bg-navy-active text-white font-medium text-sm rounded-lg"
            onClick={() => isPremium ? navigate("/dashboard") : user ? handleCheckout() : navigate("/auth")}
            disabled={loading || isPremium}
            data-testid="pricing-premium-cta"
          >
            {loading ? "Redirecting..." : ctaLabel()}
          </Button>
          <p className="mt-2 text-center text-xs text-gray-400">{selectedPlan.priceWithInterval} after trial. Cancel anytime.</p>
          <div className="mt-2 text-center">
            <LegalConsent prefix="By signing up, you agree to our" />
          </div>
        </div>
      </main>
    </div>
  );
}
